import React, { useCallback, useEffect, useState } from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Divider from "@mui/material/Divider";
import ChipSelect from "./shared/ChipSelect";
import FormCard from "./shared/FormCard";
import { executeIntent } from "../../experience/utils/IntentExecutor";
import IntentType from "../../intentexecutor/utils/IntentType";
import { useGetOrgIdentifier } from "../../experience/generic/hooks/useGetOrgIdentifier";
import { Spin } from "antd";
import apiService from "../../service/apiservice/apiService";
import { patientDataUpdateResponse } from "../../experience/carecoordinator/patient/AddEditPatientModal";
import { EventType } from "../../service/network/utils/EventType";

// ─── Helpers ──────────────────────────────────────────────────────────────────

function SectionLabel({ children }) {
  return (
    <Typography
      sx={{
        fontWeight: 700,
        fontSize: "0.7rem",
        color: "var(--main-theme-color)",
        letterSpacing: "0.07em",
        textTransform: "uppercase",
        mb: 1,
        mt: 0.25,
      }}
    >
      {children}
    </Typography>
  );
}

/** True when `options` has at least one non-empty list for the given field defs. */
function hasAnyFieldOptions(options, fieldDefs) {
  if (!options) return false;
  return fieldDefs.some((def) => Boolean(options[def.optionKey]));
}

function PreferenceChipFields({ options, formData, handleChange, fieldDefs }) {
  return fieldDefs.map((def) => {
    const chipOptions = options?.[def.optionKey];
    if (!chipOptions) return null;

    return (
      <FormCard
        key={def.optionKey}
        title={def.title}
        description={def.description}
      >
        <ChipSelect
          options={chipOptions}
          mode={def.mode}
          value={formData[def.optionKey]}
          onChange={(v) => handleChange(def.optionKey, v)}
        />
      </FormCard>
    );
  });
}

const COMMUNICATION_FIELD_DEFS = [
  {
    optionKey: "communicationStyle",
    title: "How does this patient communicate?",
    mode: "single",
  },
  {
    optionKey: "energyLevel",
    title: "Engagement level and mental status",
    mode: "single",
  },
  {
    optionKey: "languages",
    title: "Languages spoken",
    description: "Select all that apply",
    mode: "multi",
  },
  {
    optionKey: "communicationNeeds",
    title: "Communication considerations",
    description: "Select all that apply",
    mode: "multi",
  },
  {
    optionKey: "ambulatoryStatus",
    title: "Ambulatory Status",
    description: "Select all that apply",
    mode: "multi",
  },
];

const HOME_CONDITION_FIELD_DEFS = [
  {
    optionKey: "pets",
    title: "Pets in the home",
    description: "Select all that apply",
    mode: "multi",
  },
  {
    optionKey: "petManagement",
    title: "How are pets managed during visits?",
    mode: "single",
  },
  {
    optionKey: "smokingStatus",
    title: "Smoking in the home",
    mode: "single",
  },
  {
    optionKey: "othersPresent",
    title: "Who is usually present during visits?",
    mode: "single",
  },
];

const VETERAN_FIELD_DEFS = [
  {
    optionKey: "branchOfService",
    title: "Branch of Service",
    description: "Select all that apply",
    mode: "multi",
  },
  {
    optionKey: "conflictServed",
    title: "Served in Conflict",
    description: "Select all that apply",
    mode: "multi",
  },
];

const PREFERENCE_SECTION_DIVIDER_SX = {
  borderColor: "#e5e7eb",
  my: 1.75,
};

// ─── Main Component ───────────────────────────────────────────────────────────

const PatientPrefernceForm = ({
  formId,
  userIdentifier,
  onLoadingChange,
  setOpenForm,
}) => {
  const organizationIdentifier = useGetOrgIdentifier();
  const [options, setOptions] = useState(null);
  const [optionsLoading, setOptionsLoading] = useState(false);
  const [preferencesLoading, setPreferencesLoading] = useState(false);
  const [formData, setFormData] = useState({});

  const handleChange = useCallback((name, value) => {
    setFormData((prev) => ({ ...prev, [name]: value }));
  }, []);

  const setLoadingState = useCallback(
    (value) => {
      onLoadingChange?.(value);
    },
    [onLoadingChange],
  );

  const handleSubmit = useCallback(
    async (e) => {
      e.preventDefault();
      try {
        await apiService.post(
          "niramayafoundation/niramayapatient/api/v1/patient/preference",
          { organizationIdentifier, userIdentifier, preference: formData },
          "Preferences saved successfully",
          "Could not save preferences",
        );
      } finally {
        setLoadingState(false);
        setOpenForm(false);
        patientDataUpdateResponse.next({
          type: EventType.PATIENT_DATA_UPDATED,
        });
      }
    },
    [
      formData,
      organizationIdentifier,
      setLoadingState,
      setOpenForm,
      userIdentifier,
    ],
  );

  useEffect(() => {
    const getPreferenceOptions = async () => {
      setOptionsLoading(true);
      const payload = {
        configurationKey: ["niramaya.patient.preference.options"],
      };

      const result = await executeIntent(
        IntentType.GET_CONFIGURATION,
        payload,
        {
          organizationIdentifier,
        },
      );
      const options =
        result[0].configurationValue &&
        JSON.parse(result[0].configurationValue);

      setOptions(options);
      setOptionsLoading(false);
    };

    getPreferenceOptions();
  }, [organizationIdentifier]);

  useEffect(() => {
    if (!userIdentifier) return;

    const getPreference = async () => {
      setPreferencesLoading(true);
      try {
        const response = await apiService.get(
          "niramayafoundation/niramayapatient/api/v1/patient/preference",
          { organizationIdentifier, userIdentifier },
        );

        if (response) {
          setFormData((prev) => ({ ...prev, ...response }));
        }
      } finally {
        setPreferencesLoading(false);
      }
    };

    getPreference();
  }, [organizationIdentifier, userIdentifier]);

  const showCommunication = hasAnyFieldOptions(options, COMMUNICATION_FIELD_DEFS);
  const showInterests = Boolean(options?.interests);
  const showHome = hasAnyFieldOptions(options, HOME_CONDITION_FIELD_DEFS);
  const showVeteran = hasAnyFieldOptions(options, VETERAN_FIELD_DEFS);

  return (
    <Box
      component="form"
      id={formId}
      onSubmit={handleSubmit}
      noValidate
      sx={{ width: "100%", boxSizing: "border-box" }}
    >
      {preferencesLoading || optionsLoading ? (
        <Box sx={{ height: 150 }}>
          <Spin size="medium" className="spinnerCenter" />
        </Box>
      ) : (
        <>
          {/* ── Section 1: Communication & Personality ── */}
          {showCommunication && (
            <>
              <SectionLabel>Communication &amp; Personality</SectionLabel>
              <PreferenceChipFields
                options={options}
                formData={formData}
                handleChange={handleChange}
                fieldDefs={COMMUNICATION_FIELD_DEFS}
              />
              <Divider sx={PREFERENCE_SECTION_DIVIDER_SX} />
            </>
          )}

          {/* ── Section 2: Interests & Engagement ── */}
          <SectionLabel>Interests &amp; Engagement</SectionLabel>

          {showInterests && (
            <FormCard
              title="Activities & interests"
              description="Select everything that applies"
            >
              <ChipSelect
                options={options.interests}
                mode="multi"
                value={formData.interests}
                onChange={(v) => handleChange("interests", v)}
              />
            </FormCard>
          )}

          {(showHome || showVeteran) && (
            <Divider sx={PREFERENCE_SECTION_DIVIDER_SX} />
          )}

          {/* ── Section 3: Home Conditions ── */}
          {showHome && (
            <>
              <SectionLabel>Home Conditions</SectionLabel>
              <PreferenceChipFields
                options={options}
                formData={formData}
                handleChange={handleChange}
                fieldDefs={HOME_CONDITION_FIELD_DEFS}
              />
            </>
          )}

          {showHome && showVeteran && (
            <Divider sx={PREFERENCE_SECTION_DIVIDER_SX} />
          )}

          {/* Veteran Status */}
          {showVeteran && (
            <>
              <SectionLabel>Veteran Status</SectionLabel>
              <PreferenceChipFields
                options={options}
                formData={formData}
                handleChange={handleChange}
                fieldDefs={VETERAN_FIELD_DEFS}
              />
            </>
          )}
        </>
      )}
    </Box>
  );
};

export default PatientPrefernceForm;
